import { c as _c } from "react/compiler-runtime";
import { ArrowUpDown, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Medicine } from "@/entities/medicine/api/medicines";
import { translateContainerType, translateDosageForm } from "@/entities/medicine/lib/medicine-label";
import { type VariantAttribute } from "@/shared/config/constants";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
type SortableHeaderProps = {
  column: {
    getIsSorted: () => false | "asc" | "desc";
    toggleSorting: (desc?: boolean) => void;
  };
  title: string;
  align?: "left" | "center" | "right";
};
export function SortableHeader(t0) {
  const $ = _c(12);
  const {
    align: t1,
    column,
    title
  } = t0;
  const align = t1 === undefined ? "left" : t1;
  const t2 = align === "center" && "justify-center";
  const t3 = align === "right" && "justify-end";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("flex", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== column) {
    t5 = () => column.toggleSorting(column.getIsSorted() === "asc");
    $[3] = column;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowUpDown className="size-4" />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5 || $[7] !== title) {
    t7 = <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={t5}>{title}{t6}</Button>;
    $[6] = t5;
    $[7] = title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}
export function MetricCard(t0) {
  const $ = _c(17);
  const {
    description,
    icon: Icon,
    title,
    value
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <CardDescription>{title}</CardDescription>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon) {
    t2 = <div className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded-full"><Icon className="size-4" /></div>;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between gap-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== value) {
    t4 = <CardTitle className="text-3xl capitalize">{value}</CardTitle>;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <CardHeader className="pb-2">{t3}{t4}</CardHeader>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== description) {
    t6 = <CardContent><p className="text-muted-foreground text-sm">{description}</p></CardContent>;
    $[12] = description;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <Card className="shadow-xs">{t5}{t6}</Card>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function InlineMetric(t0) {
  const $ = _c(10);
  const {
    badge,
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-xs text-slate-700 dark:text-slate-300">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== badge || $[3] !== t1) {
    t2 = <div className="flex items-start justify-between gap-2">{t1}{badge}</div>;
    $[2] = badge;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== value) {
    t3 = <p className="pt-1 text-lg font-semibold text-slate-900 dark:text-slate-50">{value}</p>;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="rounded-lg border border-slate-300 bg-linear-to-b from-white from-0% via-slate-200 via-15% to-slate-400 to-100% p-3 shadow-xs dark:border-slate-700 dark:from-slate-600 dark:via-slate-800 dark:via-15% dark:to-slate-900">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function EmptyState(t0) {
  const $ = _c(12);
  const {
    description,
    icon: Icon,
    title
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <div className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full"><Icon className="size-4" /></div>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <p className="font-medium">{title}</p>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <p className="text-muted-foreground text-sm">{description}</p>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="space-y-1">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="bg-muted/20 flex h-full min-h-48 w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-6 text-center">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function DetailBadge(t0) {
  const $ = _c(5);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-slate-600">{label}:</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== value) {
    t2 = <Badge variant="outline" className="gap-1 border-blue-400/80 bg-white/70 text-slate-900">{t1} {value}</Badge>;
    $[2] = t1;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function SpinningRefreshIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(className, "animate-spin");
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <RefreshCw className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function DosageFormBadge(t0) {
  const $ = _c(7);
  const {
    form
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== form) {
    t1 = translateDosageForm(form) ?? form;
    $[0] = form;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const translatedForm = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("medicines.labels.form");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== translatedForm) {
    t3 = <DetailBadge label={t2} value={translatedForm} />;
    $[4] = t2;
    $[5] = translatedForm;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function VariantValue(t0) {
  const $ = _c(23);
  const {
    attribute,
    medicine
  } = t0;
  const {
    t
  } = useTranslation();
  if (attribute === "dosage_form") {
    let t1;
    if ($[0] !== medicine.dosage_form) {
      t1 = medicine.dosage_form ? <DosageFormBadge form={medicine.dosage_form} /> : <Badge variant="outline">—</Badge>;
      $[0] = medicine.dosage_form;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (attribute === "container_type") {
    let t1;
    if ($[2] !== t) {
      t1 = t("medicines.labels.containerType");
      $[2] = t;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== medicine.container_type) {
      t2 = medicine.container_type ? translateContainerType(String(medicine.container_type)) ?? String(medicine.container_type) : "\u2014";
      $[4] = medicine.container_type;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t1 || $[7] !== t2) {
      t3 = <DetailBadge label={t1} value={t2} />;
      $[6] = t1;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (attribute === "pack") {
    let t1;
    if ($[9] !== t) {
      t1 = t("medicines.labels.pack");
      $[9] = t;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== medicine.pack || $[12] !== t) {
      t2 = medicine.pack != null ? t("medicines.labels.packPieces", {
        count: medicine.pack
      }) : "\u2014";
      $[11] = medicine.pack;
      $[12] = t;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t1 || $[15] !== t2) {
      t3 = <DetailBadge label={t1} value={t2} />;
      $[14] = t1;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  const label = medicine[attribute];
  let t1;
  if ($[17] !== attribute || $[18] !== t) {
    t1 = attribute === "strength" ? t("medicines.labels.strength") : attribute === "volume" ? t("medicines.labels.volume") : attribute === "vendor_country" ? t("medicines.labels.country") : attribute;
    $[17] = attribute;
    $[18] = t;
    $[19] = t1;
  } else {
    t1 = $[19];
  }
  const attributeLabel = t1;
  const t2 = label ? String(label) : "\u2014";
  let t3;
  if ($[20] !== attributeLabel || $[21] !== t2) {
    t3 = <DetailBadge label={attributeLabel} value={t2} />;
    $[20] = attributeLabel;
    $[21] = t2;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
}